import React, { useState, useEffect, useRef } from 'react';
import { adminUserService } from '../../services/adminUserService';
import UserEditModal from './UserEditModal';
import UserDetailModal from './UserDetailModal';
import UserCreateModal from './UserCreateModal';
import GoldenDeleteConfirmation from '../../components/GoldenDeleteConfirmation';
import GoldenNotification from './GoldenNotification';
import AdminLoading from './AdminLoading';
import './UserManagement.css';
import { getApiUrl } from '../../config/environment';
import { isAdminRoleUser } from '../utils/userRoleUtils';
import { parseThrownAdminError } from '../utils/adminApiError';
import { extractSheenBackendErrorMessageLoose } from '../../utils/apiErrorMessage';
import { writeCreatorStorefrontToStorage } from '../../utils/creatorStorefront';

const UserManagement = ({ navigationParams = {} }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ 
    page: 1, 
    limit: 20, 
    total: 0, 
    totalPages: 0 
  });
  const [filters, setFilters] = useState({ 
    search: '', 
    role: '', 
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  
  // View mode: 'active' or 'deleted'
  const [viewMode, setViewMode] = useState('active');
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [deletedUsersLoading, setDeletedUsersLoading] = useState(false);
  const [deletedUsersPagination, setDeletedUsersPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalType, setCreateModalType] = useState('user'); // 'user' | 'team'

  const openedUserFromNavRef = useRef(null);
  
  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    type: 'success',
    title: '',
    message: '',
    duration: 3000
  });

  // Fetch users from API
  const fetchUsers = async (updateCountOnly = false) => {
    if (!updateCountOnly) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log('🔄 Fetching users with params:', { 
        page: updateCountOnly ? 1 : pagination.page,
        limit: updateCountOnly ? 1 : pagination.limit,
        ...(updateCountOnly ? {} : filters)
      });
      
      const response = await adminUserService.getUsers({
        page: updateCountOnly ? 1 : pagination.page,
        limit: updateCountOnly ? 1 : pagination.limit,
        ...(updateCountOnly ? {} : filters)
      });
      
      console.log('📥 Users API Response:', response);
      console.log('📥 Response structure:', {
        hasSuccess: !!response.success,
        hasData: !!response.data,
        hasTotal: !!response.total,
        hasPagination: !!response.pagination,
        dataLength: response.data?.length,
        paginationTotal: response.pagination?.total,
        paginationTotalPages: response.pagination?.totalPages
      });
      
      if (response.success) {
        const usersData = response.data || response.users || [];
        
        if (!updateCountOnly) {
          setUsers(usersData);
        }
        
        // Handle different response structures for pagination
        let total = 0;
        let totalPages = 0;
        
        if (response.pagination) {
          total = response.pagination.total || response.pagination.count || 0;
          totalPages = response.pagination.totalPages || response.pagination.pages || 0;
        } else if (response.total !== undefined) {
          total = response.total;
          totalPages = response.totalPages || Math.ceil(total / pagination.limit);
        } else {
          // Fallback: use data length if no pagination info
          total = usersData.length;
          totalPages = 1;
        }
        
        setPagination(prev => {
          // Update total if we got a valid number from API (including 0), otherwise preserve existing count
          const newTotal = (total !== undefined && total !== null && !isNaN(total)) ? total : prev.total;
          return {
            ...prev,
            total: newTotal,
            totalPages: totalPages || 1
          };
        });
        
        console.log('✅ Users loaded successfully:', usersData.length, 'users');
        console.log('✅ Pagination updated - Total:', total || 'preserved', 'Total Pages:', totalPages);
      } else {
        if (!updateCountOnly) {
          setError(response.message || 'Failed to fetch users');
        }
        console.error('❌ Failed to fetch users:', response);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      if (!updateCountOnly) {
        setError('Failed to fetch users. Please try again.');
      }
    } finally {
      if (!updateCountOnly) {
        setLoading(false);
      }
    }
  };

  // Fetch deleted users from API
  const fetchDeletedUsers = async (updateCountOnly = false) => {
    if (!updateCountOnly) {
      setDeletedUsersLoading(true);
    }
    setError(null);
    
    try {
      console.log('🔄 Fetching deleted users with params:', { 
        page: deletedUsersPagination.page,
        limit: deletedUsersPagination.limit,
        search: filters.search 
      });
      
      const response = await adminUserService.getDeletedUsers({
        page: updateCountOnly ? 1 : deletedUsersPagination.page,
        limit: updateCountOnly ? 1 : deletedUsersPagination.limit,
        search: updateCountOnly ? '' : filters.search
      });
      
      console.log('📥 Deleted Users API Response:', response);
      
      if (response.success) {
        const deletedData = response.data || response.deletedUsers || [];
        
        if (!updateCountOnly) {
          setDeletedUsers(deletedData);
        }
        
        // Handle pagination for deleted users
        let total = 0;
        let totalPages = 0;
        
        if (response.pagination) {
          total = response.pagination.total || response.pagination.count || 0;
          totalPages = response.pagination.totalPages || response.pagination.pages || 0;
        } else if (response.total !== undefined) {
          total = response.total;
          totalPages = response.totalPages || Math.ceil(total / deletedUsersPagination.limit);
        } else {
          total = deletedData.length;
          totalPages = 1;
        }
        
        setDeletedUsersPagination(prev => ({
          ...prev,
          total: total,
          totalPages: totalPages || 1
        }));
        
        console.log('✅ Deleted users loaded successfully:', deletedData.length, 'users');
        console.log('✅ Deleted users total count:', total);
      } else {
        if (!updateCountOnly) {
          setError(response.message || 'Failed to fetch deleted users');
        }
        console.error('❌ Failed to fetch deleted users:', response);
      }
    } catch (error) {
      console.error('❌ Error fetching deleted users:', error);
      if (!updateCountOnly) {
        setError('Failed to fetch deleted users. Please try again.');
      }
    } finally {
      if (!updateCountOnly) {
        setDeletedUsersLoading(false);
      }
    }
  };

  // Fetch all users counts (combined - recommended)
  const fetchUsersCounts = async () => {
    try {
      console.log('🔄 Fetching users counts from API...');
      const response = await adminUserService.getUsersCounts();
      
      console.log('📥 Users Counts API Response:', response);
      console.log('📥 Response.data:', response.data);
      console.log('📥 Response.data.active:', response.data?.active);
      console.log('📥 Response.data.deleted:', response.data?.deleted);
      console.log('📥 Response.data.total:', response.data?.total);
      
      if (response.success && response.data) {
        const active = response.data.active !== undefined ? response.data.active : (response.data.count?.active || 0);
        const deleted = response.data.deleted !== undefined ? response.data.deleted : (response.data.count?.deleted || 0);
        const total = response.data.total !== undefined ? response.data.total : (response.data.count?.total || 0);
        
        console.log('📊 Extracted counts:', { active, deleted, total });
        
        // Update active users count
        if (active !== undefined && active !== null) {
          setPagination(prev => ({
            ...prev,
            total: active
          }));
          console.log('✅ Active users count set to:', active);
        }
        
        // Update deleted users count
        if (deleted !== undefined && deleted !== null) {
          setDeletedUsersPagination(prev => ({
            ...prev,
            total: deleted
          }));
          console.log('✅ Deleted users count set to:', deleted);
        }
        
        console.log('✅ Users counts updated:', { active, deleted, total });
      } else {
        console.error('❌ Failed to fetch users counts - response structure:', response);
        // Fallback: try individual count APIs
        await fetchIndividualCounts();
      }
    } catch (error) {
      console.error('❌ Error fetching users counts:', error);
      console.error('❌ Error details:', error.message);
      // Fallback: try individual count APIs
      await fetchIndividualCounts();
    }
  };

  // Fallback: Fetch individual counts if combined API fails
  const fetchIndividualCounts = async () => {
    try {
      console.log('🔄 Fetching individual counts as fallback...');
      
      const [activeRes, deletedRes, totalRes] = await Promise.all([
        adminUserService.getActiveUsersCount(),
        adminUserService.getDeletedUsersCount(),
        adminUserService.getTotalUsersCount()
      ]);
      
      console.log('📥 Active Count Response:', activeRes);
      console.log('📥 Deleted Count Response:', deletedRes);
      console.log('📥 Total Count Response:', totalRes);
      
      if (activeRes.success && activeRes.data) {
        const activeCount = activeRes.data.count || activeRes.data.active || 0;
        setPagination(prev => ({
          ...prev,
          total: activeCount
        }));
        console.log('✅ Active users count updated:', activeCount);
      } else {
        console.error('❌ Failed to fetch active count:', activeRes);
      }
      
      if (deletedRes.success && deletedRes.data) {
        const deletedCount = deletedRes.data.count || deletedRes.data.deleted || 0;
        setDeletedUsersPagination(prev => ({
          ...prev,
          total: deletedCount
        }));
        console.log('✅ Deleted users count updated:', deletedCount);
      } else {
        console.error('❌ Failed to fetch deleted count:', deletedRes);
      }
    } catch (error) {
      console.error('❌ Error fetching individual counts:', error);
      console.error('❌ Error details:', error.message);
    }
  };

  // Load both counts on component mount
  useEffect(() => {
    // Fetch all counts using combined API (recommended)
    fetchUsersCounts();
  }, []);

  // Load users based on view mode and filters
  useEffect(() => {
    if (viewMode === 'active') {
      fetchUsers();
    } else {
      fetchDeletedUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, deletedUsersPagination.page, filters.search, filters.role, filters.status, filters.sortBy, filters.sortOrder, viewMode]);

  // Handle user edit
  const handleEditUser = (user) => {
    console.log('✏️ Opening edit modal for user:', user.name);
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  // Handle user detail view
  const handleViewUserDetails = async (user) => {
    console.log('👁️ Opening user details for:', user.name);
    setSelectedUser(user);
    setDetailModalOpen(true);
    setUserDetailsLoading(true);
    
    try {
      console.log('🔄 Fetching detailed user data for:', user._id);
      const response = await adminUserService.getUser(user._id);
      console.log('📥 User Details API Response:', response);
      
      if (response && response.success) {
        let details = response.data || {};
        const uid = user._id || user.id;
        if (uid) {
          const [cartRes, wishRes, visitsRes] = await Promise.all([
            adminUserService.getUserCartSnapshot(uid),
            adminUserService.getUserWishlistSnapshot(uid),
            adminUserService.getUserRecentVisitsSnapshot(uid),
          ]);
          const unwrapSnap = (res) => {
            if (!res || res.success === false || res.status === 404) return undefined;
            if (res.data !== undefined && res.data !== null) return res.data;
            if (
              Array.isArray(res) ||
              res.items ||
              res.products ||
              res.visits ||
              res.views
            ) {
              return res;
            }
            return undefined;
          };
          details = {
            ...details,
            _adminShopExtras: {
              cart: unwrapSnap(cartRes),
              wishlist: unwrapSnap(wishRes),
              recentVisits: unwrapSnap(visitsRes),
            },
          };
        }
        setUserDetails(details);
        console.log('✅ User details loaded successfully');
      } else {
        console.error('❌ Failed to fetch user details:', response);
        setUserDetails(user); // Fallback to basic user data
      }
    } catch (error) {
      console.error('❌ Error fetching user details:', error);
      setUserDetails(user); // Fallback to basic user data
    } finally {
      setUserDetailsLoading(false);
    }
  };

  useEffect(() => {
    const uid = navigationParams?.openUserId;
    if (!uid) {
      openedUserFromNavRef.current = null;
      return;
    }
    if (loading || !users?.length) return;
    if (openedUserFromNavRef.current === uid) return;
    const u = users.find((x) => String(x._id || x.id) === String(uid));
    if (u) {
      openedUserFromNavRef.current = uid;
      handleViewUserDetails(u);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationParams?.openUserId, users, loading]);

  // Handle user creation
  const handleCreateUser = () => {
    console.log('➕ Opening create user modal');
    setCreateModalType('user');
    setCreateModalOpen(true);
  };

  // Handle team member creation
  const handleCreateTeamMember = () => {
    console.log('➕ Opening add team member modal');
    setCreateModalType('team');
    setCreateModalOpen(true);
  };

  // Handle user creation save
  const handleCreateSave = async (userData) => {
    try {
      console.log('💾 Creating new user:', userData);
      console.log('💾 User Data to send:', JSON.stringify(userData, null, 2));
      
      // First, test the API endpoint
      console.log('🔍 Testing API endpoint before creating user...');
      const testUrl = getApiUrl('/admin/users');
      console.log('🔍 API URL:', testUrl);
      
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      console.log('🔍 Admin Token:', adminToken ? 'Present' : 'Missing');
      
      if (!adminToken) {
        alert('❌ No admin token found. Please login as admin first.');
        return;
      }
      
      const response = await adminUserService.createUser(userData);
      console.log('📥 Create User API Response:', response);
      
      if (response && response.success) {
        console.log('✅ User created successfully');
        setCreateModalOpen(false);
        await fetchUsers(); // Refresh the list to update total count
        // Update counts
        await fetchUsersCounts();
        setNotificationData({
          type: 'success',
          title: 'User Created Successfully',
          message: 'New user has been created successfully.',
          duration: 4000
        });
        setShowNotification(true);
      } else {
        console.error('❌ Failed to create user:', response);
        const errorMessage = response?.message || response?.error || 'Unknown error occurred';
        alert(`Failed to create user: ${errorMessage}`);
      }
    } catch (error) {
      console.error('❌ Error creating user:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      
      // Check if it's a 404 error specifically
      if (error.message && error.message.includes('Not Found')) {
        alert(`❌ API Endpoint Not Found: The create user endpoint may not be implemented in the backend. Please check:\n\n1. Backend server is running on port 5000\n2. POST /api/admin/users endpoint exists\n3. Admin authentication is working\n\nCheck console for detailed error information.`);
      } else {
        alert(`Error creating user: ${error.message || 'Please check console for details'}`);
      }
    }
  };

  // Handle user edit save
  const handleEditSave = async (updatedUserData) => {
    try {
      const {
        newPassword,
        confirmPassword,
        accessPermissions,
        creatorBannerUrl,
        creatorHeadline,
        creatorBio,
        ...rest
      } = updatedUserData;
      const isTeamWithAccess = rest.role === 'team' && accessPermissions && Object.keys(accessPermissions).length > 0;
      
      // 1. Password update (if requested)
      if (newPassword && newPassword.trim()) {
        try {
          const pwdResponse = await adminUserService.updateUserPassword(
            selectedUser._id || selectedUser.id,
            newPassword.trim()
          );
          if (!pwdResponse?.success) {
            const msg =
              typeof pwdResponse === 'object' && pwdResponse !== null
                ? extractSheenBackendErrorMessageLoose(pwdResponse, pwdResponse.status || 0)
                : pwdResponse?.error || pwdResponse?.message || 'Password update failed.';
            setNotificationData({
              type: 'error',
              title: 'Password Update Failed',
              message: msg,
              duration: 6000
            });
            setShowNotification(true);
            return;
          }
        } catch (pwdError) {
          const parsed = parseThrownAdminError(pwdError);
          const msg = parsed.message || pwdError?.message || '';
          const is404 = msg.includes('404') || msg.includes('Not Found');
          setNotificationData({
            type: 'error',
            title: 'Password Update Failed',
            message: is404 ? 'API not found. Use: PUT /api/admin/users/:userId/password with { "newPassword" }' : msg,
            duration: 6000
          });
          setShowNotification(true);
          return;
        }
      }
      
      // 2. Main user update (without accessPermissions - handled separately)
      const updateData = {
        name: rest.name?.trim(),
        email: rest.email?.trim(),
        phone: rest.phone?.trim() || undefined,
        role: rest.role ? String(rest.role).toLowerCase() : undefined,
        isActive: rest.isActive !== undefined ? rest.isActive : true,
        isVerified: rest.isVerified !== undefined ? rest.isVerified : undefined,
        addresses: rest.addresses
      };
      if (rest.role === 'team') {
        updateData.creatorBannerUrl = creatorBannerUrl ?? '';
        updateData.creatorHeadline = creatorHeadline ?? '';
        updateData.creatorBio = creatorBio ?? '';
      }
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) delete updateData[key];
      });
      
      console.log('📤 Sending user update:', updateData);
      const response = await adminUserService.updateUser(selectedUser._id, updateData);
      
      if (!response || response.success === false) {
        console.error('❌ Update failed:', response);
        setNotificationData({
          type: 'error',
          title: 'Update Failed',
          message: response?.error || response?.message || 'Failed to update user',
          duration: 5000
        });
        setShowNotification(true);
        return;
      }
      
      if (!response.data) {
        console.error('❌ No data in response:', response);
        setNotificationData({
          type: 'error',
          title: 'Update Failed',
          message: 'Update response missing data',
          duration: 5000
        });
        setShowNotification(true);
        return;
      }
      
      // 3. Access permissions update - PUT /api/admin/users/:userId/access
      let accessUpdateSuccess = true;
      if (isTeamWithAccess) {
        try {
          console.log('📤 Sending access update:', accessPermissions);
          const accessResponse = await adminUserService.updateUserAccess(selectedUser._id, accessPermissions);
          if (!accessResponse?.success) {
            accessUpdateSuccess = false;
            console.error('❌ Access update failed:', accessResponse);
          } else {
            console.log('✅ Access permissions saved to backend');
          }
        } catch (accessError) {
          accessUpdateSuccess = false;
          const msg = accessError?.message || '';
          const is404 = msg.includes('404') || msg.includes('Not Found');
          console.error('❌ Access update error:', accessError);
          setNotificationData({
            type: is404 ? 'warning' : 'error',
            title: is404 ? 'User Updated, Access Not Saved' : 'Access Update Failed',
            message: is404
              ? 'Backend mein add karein: PUT /api/admin/users/:userId/access with body { accessPermissions }'
              : (msg || 'Access list backend mein save nahi hua'),
            duration: 6000
          });
          setShowNotification(true);
          setEditModalOpen(false);
          setSelectedUser(null);
          await fetchUsers();
          await fetchUsersCounts();
          return;
        }
      }
      
      const updatedUser = response.data;
      const roleMismatch = updateData.role && updatedUser.role !== updateData.role;

      const editedUserId = selectedUser._id || selectedUser.id;
      if (rest.role === 'team' && editedUserId) {
        writeCreatorStorefrontToStorage(editedUserId, {
          bannerUrl: updateData.creatorBannerUrl || '',
          headline: updateData.creatorHeadline || '',
          bio: updateData.creatorBio || '',
          creatorSlug: updatedUser.creatorSlug || selectedUser.creatorSlug || '',
          name: updateData.name || selectedUser.name,
          role: 'team',
          updatedAt: new Date().toISOString(),
        });
      }
      
      setEditModalOpen(false);
      setSelectedUser(null);
      await fetchUsers();
      await fetchUsersCounts();
      
      // Success popup - show after successful push to backend
      let successMsg = 'User information updated successfully.';
      if (newPassword && isTeamWithAccess && accessUpdateSuccess) {
        successMsg = 'User info, password and access list updated successfully.';
      } else if (newPassword) {
        successMsg = 'User info and password updated successfully.';
      } else if (isTeamWithAccess && accessUpdateSuccess) {
        successMsg = 'User info and access list updated successfully.';
      }
      if (roleMismatch) successMsg = `Role mismatch warning. ${successMsg}`;
      
      setNotificationData({
        type: roleMismatch ? 'warning' : 'success',
        title: 'User Updated Successfully',
        message: successMsg,
        duration: 5000
      });
      setShowNotification(true);
    } catch (error) {
      console.error('❌ Error updating user:', error);
      const errMsg = typeof error?.message === 'string' && error.message.startsWith('{')
        ? (() => { try { const p = JSON.parse(error.message); return p.error || p.message || error.message; } catch { return error.message; } })()
        : error?.message || 'Failed to update user. Please try again.';
      setNotificationData({
        type: 'error',
        title: 'Update Error',
        message: errMsg,
        duration: 5000
      });
      setShowNotification(true);
    }
  };

  // Handle user delete
  const handleDeleteUser = (user) => {
    if (isAdminRoleUser(user)) {
      setNotificationData({
        type: 'error',
        title: 'Cannot delete',
        message: 'Admin accounts cannot be deleted.',
        duration: 5000,
      });
      setShowNotification(true);
      return;
    }
    console.log('🗑️ Opening delete confirmation for user:', user.name);
    console.log('🗑️ User ID to delete:', user._id);
    console.log('🗑️ Full user object:', user);
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  // Confirm user delete
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    if (isAdminRoleUser(userToDelete)) {
      setDeleteModalOpen(false);
      setUserToDelete(null);
      setNotificationData({
        type: 'error',
        title: 'Cannot delete',
        message: 'Admin accounts cannot be deleted.',
        duration: 5000,
      });
      setShowNotification(true);
      return;
    }

    try {
      console.log('🗑️ Deleting user:', userToDelete.name);
      console.log('🗑️ User ID:', userToDelete._id);
      console.log('🗑️ User Object:', userToDelete);
      
      // First, verify the user exists by fetching their details
      console.log('🔍 Verifying user exists before deletion...');
      const verifyResponse = await adminUserService.getUser(userToDelete._id);
      console.log('📥 Verify API Response:', verifyResponse);
      
      if (!verifyResponse || !verifyResponse.success) {
        console.error('❌ User not found or verification failed:', verifyResponse);
        alert(`User not found: ${verifyResponse?.message || 'User may have been already deleted'}`);
        setDeleteModalOpen(false);
        setUserToDelete(null);
        fetchUsers(); // Refresh the list
        return;
      }
      
      console.log('✅ User verified, proceeding with deletion...');
      
      const response = await adminUserService.deleteUser(userToDelete._id);
      console.log('📥 Delete API Response:', response);
      
      if (response && response.success) {
        console.log('✅ User deleted successfully');
        setDeleteModalOpen(false);
        setUserToDelete(null);
        // Refresh both lists
        await fetchUsers();
        await fetchDeletedUsers();
        // Update counts
        await fetchUsersCounts();
        setNotificationData({
          type: 'success',
          title: 'User Deleted Successfully',
          message: `${userToDelete.name} has been deleted successfully.`,
          duration: 4000
        });
        setShowNotification(true);
      } else {
        console.error('❌ Failed to delete user:', response);
        const errorMessage = response?.message || response?.error || 'Unknown error occurred';
        setNotificationData({
          type: 'error',
          title: 'Delete Failed',
          message: errorMessage,
          duration: 5000
        });
        setShowNotification(true);
      }
    } catch (error) {
      console.error('❌ Error deleting user:', error);
      const parsed = parseThrownAdminError(error);
      const msg = parsed.message || 'Please check console for details';
      const title =
        parsed.code === 'CANNOT_DELETE_ADMIN' ? 'Cannot delete' : 'Delete Error';

      if (msg.includes('Not Found')) {
        setNotificationData({
          type: 'error',
          title: 'User Not Found',
          message:
            'The user may have been already deleted or doesn\'t exist. Please refresh the user list.',
          duration: 5000,
        });
        setShowNotification(true);
        fetchUsers();
      } else {
        setNotificationData({
          type: 'error',
          title,
          message: msg,
          duration: 5000,
        });
        setShowNotification(true);
      }
    }
  };

  // Handle user restore
  const handleRestoreUser = async (user) => {
    if (!window.confirm(`Are you sure you want to restore ${user.name}?`)) {
      return;
    }
    
    try {
      console.log('🔄 Restoring user:', user.name);
      
      const response = await adminUserService.restoreUser(user._id);
      
      if (response.success) {
        console.log('✅ User restored successfully');
        // Refresh both lists to ensure consistency
        await fetchUsers();
        if (viewMode === 'deleted') {
          await fetchDeletedUsers();
        }
        // Update counts
        await fetchUsersCounts();
        setNotificationData({
          type: 'success',
          title: 'User Restored Successfully',
          message: 'User has been restored successfully.',
          duration: 4000
        });
        setShowNotification(true);
      } else {
        console.error('❌ Failed to restore user:', response);
        alert('Failed to restore user. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error restoring user:', error);
      alert('Error restoring user. Please try again.');
    }
  };

  // Debug function to test API endpoint
  const testDeleteAPI = async () => {
    if (!userToDelete) {
      alert('Please select a user first');
      return;
    }
    
    try {
      console.log('🧪 Testing delete API endpoint...');
      console.log('🧪 User ID:', userToDelete._id);
      
      // Test the exact URL that will be called
      const testUrl = getApiUrl(`/admin/users/${userToDelete._id}`);
      console.log('🧪 Test URL:', testUrl);
      
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      console.log('🧪 Admin Token:', adminToken ? 'Present' : 'Missing');
      
      const response = await fetch(testUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🧪 Response Status:', response.status);
      console.log('🧪 Response Headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await response.text();
      console.log('🧪 Response Data:', data);
      
      if (response.ok) {
        alert('✅ API Test Successful! User can be deleted.');
      } else {
        alert(`❌ API Test Failed: ${response.status} - ${data}`);
      }
    } catch (error) {
      console.error('🧪 API Test Error:', error);
      alert(`❌ API Test Error: ${error.message}`);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Reset to first page for the appropriate view mode
    if (viewMode === 'active') {
      setPagination(prev => ({ ...prev, page: 1 }));
    } else {
      setDeletedUsersPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ 
      search: '', 
      role: '', 
      status: '',
      sortBy: viewMode === 'deleted' ? 'deletedAt' : 'createdAt',
      sortOrder: 'desc'
    });
    if (viewMode === 'active') {
      setPagination(prev => ({ ...prev, page: 1 }));
    } else {
      setDeletedUsersPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const displayedUsers = viewMode === 'active' ? users : deletedUsers;

  const renderUserActions = (user) => (
    <div className="action-buttons">
      <button
        type="button"
        onClick={() => handleViewUserDetails(user)}
        className="view-btn"
        title="View User Details"
      >
        <span className="action-btn-icon" aria-hidden>👁️</span>
        <span className="action-btn-label">View</span>
      </button>
      <button
        type="button"
        onClick={() => handleEditUser(user)}
        className="edit-btn"
        title="Edit User"
      >
        <span className="action-btn-icon" aria-hidden>✏️</span>
        <span className="action-btn-label">Edit</span>
      </button>
      {viewMode === 'deleted' ? (
        <button
          type="button"
          onClick={() => handleRestoreUser(user)}
          className="restore-btn"
          title="Restore User"
        >
          <span className="action-btn-icon" aria-hidden>🔄</span>
          <span className="action-btn-label">Restore</span>
        </button>
      ) : (
        <>
          {user.isDeleted ? (
            <button
              type="button"
              onClick={() => handleRestoreUser(user)}
              className="restore-btn"
              title="Restore User"
            >
              <span className="action-btn-icon" aria-hidden>🔄</span>
              <span className="action-btn-label">Restore</span>
            </button>
          ) : isAdminRoleUser(user) ? (
            <span
              className="delete-btn delete-btn--disabled"
              title="Admin accounts cannot be deleted"
            >
              <span className="action-btn-icon" aria-hidden>🗑️</span>
              <span className="action-btn-label">Delete</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => handleDeleteUser(user)}
              className="delete-btn"
              title="Delete User"
            >
              <span className="action-btn-icon" aria-hidden>🗑️</span>
              <span className="action-btn-label">Delete</span>
            </button>
          )}
        </>
      )}
    </div>
  );

  if (loading && users.length === 0) {
    return (
      <AdminLoading
        title="Loading Users"
        subtitle="Fetching user accounts..."
        icon="👥"
      />
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>User Management</h2>
        <div className="user-stats">
          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn ${viewMode === 'active' ? 'active' : ''}`}
              onClick={() => setViewMode('active')}
            >
              👥 Active Users ({pagination.total})
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'deleted' ? 'active' : ''}`}
              onClick={() => setViewMode('deleted')}
            >
              🗑️ Deleted Users ({deletedUsersPagination.total})
            </button>
          </div>
          <span className="total-users">
            Total: {viewMode === 'active' ? pagination.total : deletedUsersPagination.total}
          </span>
          <span className="current-page">
            Page: {viewMode === 'active' ? pagination.page : deletedUsersPagination.page} of {viewMode === 'active' ? pagination.totalPages : deletedUsersPagination.totalPages}
          </span>
          {viewMode === 'active' && (
            <>
              <button 
                onClick={handleCreateUser}
                className="add-user-btn"
                title="Add New User"
              >
                ➕ Add User
              </button>
              <button 
                onClick={handleCreateTeamMember}
                className="add-team-member-btn"
                title="Add Team Member"
              >
                👥 Add Team Member
              </button>
              {userToDelete && (
                <button 
                  onClick={testDeleteAPI}
                  className="debug-btn"
                  title="Test Delete API"
                >
                  🧪 Test API
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder={viewMode === 'deleted' ? 'Search deleted users...' : 'Search by name, email, or phone...'}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          {viewMode === 'active' && (
            <>
              <div className="filter-group">
                <label>Role:</label>
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Roles</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="team">Team Member</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Status:</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Sort By:</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="filter-select"
                >
                  <option value="createdAt">Created Date</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Order:</label>
                <select
                  value={filters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  className="filter-select"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </>
          )}
          
          {viewMode === 'deleted' && (
            <div className="filter-group">
              <label>Sort By:</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="filter-select"
              >
                <option value="deletedAt">Deleted Date</option>
                <option value="createdAt">Created Date</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
              </select>
            </div>
          )}
          
          <button onClick={clearFilters} className="clear-filters-btn">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
          <button onClick={fetchUsers} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {/* Users — mobile cards + desktop table */}
      <div className="users-table-container">
        <div className="users-mobile-list" aria-label="Users">
          {displayedUsers.map((user) => (
            <article
              key={user._id}
              className={`user-mgmt-card${user.isDeleted ? ' user-mgmt-card--muted' : ''}`}
            >
              <div className="user-mgmt-card__top">
                <div className="user-mgmt-card__identity">
                  {user.avatar?.url ? (
                    <img
                      src={user.avatar.url}
                      alt=""
                      className="user-mgmt-card__avatar"
                    />
                  ) : (
                    <div className="user-mgmt-card__avatar user-mgmt-card__avatar--initial" aria-hidden>
                      {(user.name || user.email || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="user-mgmt-card__title-block">
                    <h3 className="user-mgmt-card__name">{user.name || 'Unnamed'}</h3>
                    <p className="user-mgmt-card__email">{user.email}</p>
                  </div>
                </div>
                <div className="user-mgmt-card__badges">
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'team' ? 'Team' : user.role}
                  </span>
                  {viewMode === 'deleted' ? (
                    <span className="deleted-badge">Deleted</span>
                  ) : (
                    <>
                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {user.isDeleted && <span className="deleted-badge">Deleted</span>}
                    </>
                  )}
                </div>
              </div>
              <dl className="user-mgmt-card__details">
                <div className="user-mgmt-card__detail-row">
                  <dt>Phone</dt>
                  <dd>{user.phone || 'N/A'}</dd>
                </div>
                <div className="user-mgmt-card__detail-row">
                  <dt>Joined</dt>
                  <dd>{formatDate(user.createdAt)}</dd>
                </div>
                {viewMode === 'deleted' && (
                  <div className="user-mgmt-card__detail-row">
                    <dt>Deleted</dt>
                    <dd>{user.deletedAt ? formatDate(user.deletedAt) : 'N/A'}</dd>
                  </div>
                )}
              </dl>
              <footer className="user-mgmt-card__footer">{renderUserActions(user)}</footer>
            </article>
          ))}
        </div>

        <table className="users-table users-table--desktop">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              {viewMode === 'deleted' && <th>Deleted At</th>}
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedUsers.map((user) => (
              <tr key={user._id} className={user.isDeleted ? 'deleted-user' : ''}>
                <td>
                  <div className="user-info">
                    {user.avatar?.url && (
                      <img
                        src={user.avatar.url}
                        alt={user.name}
                        className="user-avatar"
                      />
                    )}
                    <span className="user-name">{user.name}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>{user.phone || 'N/A'}</td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'team' ? 'Team Member' : user.role}
                  </span>
                </td>
                <td>
                  {viewMode === 'deleted' ? (
                    <span className="deleted-badge">Deleted</span>
                  ) : (
                    <>
                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {user.isDeleted && <span className="deleted-badge">Deleted</span>}
                    </>
                  )}
                </td>
                {viewMode === 'deleted' && (
                  <td>{user.deletedAt ? formatDate(user.deletedAt) : 'N/A'}</td>
                )}
                <td>{formatDate(user.createdAt)}</td>
                <td>{renderUserActions(user)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {viewMode === 'active' && users.length === 0 && !loading && (
          <div className="no-users">
            <p>No users found matching your criteria.</p>
          </div>
        )}
        {viewMode === 'deleted' && deletedUsers.length === 0 && !deletedUsersLoading && (
          <div className="no-users">
            <p>No deleted users found.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(viewMode === 'active' ? pagination.totalPages : deletedUsersPagination.totalPages) > 1 && (
        <div className="pagination">
          <button 
            onClick={() => {
              if (viewMode === 'active') {
                handlePageChange(pagination.page - 1);
              } else {
                setDeletedUsersPagination(prev => ({ ...prev, page: prev.page - 1 }));
              }
            }}
            disabled={viewMode === 'active' ? pagination.page === 1 : deletedUsersPagination.page === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {viewMode === 'active' ? pagination.page : deletedUsersPagination.page} of {viewMode === 'active' ? pagination.totalPages : deletedUsersPagination.totalPages}
          </span>
          
          <button 
            onClick={() => {
              if (viewMode === 'active') {
                handlePageChange(pagination.page + 1);
              } else {
                setDeletedUsersPagination(prev => ({ ...prev, page: prev.page + 1 }));
              }
            }}
            disabled={viewMode === 'active' ? pagination.page === pagination.totalPages : deletedUsersPagination.page === deletedUsersPagination.totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && selectedUser && (
        <UserEditModal
          user={selectedUser}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedUser(null);
          }}
          onSave={handleEditSave}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && userToDelete && (
        <GoldenDeleteConfirmation
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setUserToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete User"
          message={`Are you sure you want to delete ${userToDelete.name}?`}
          userInfo={{
            name: userToDelete.name,
            email: userToDelete.email,
            role: userToDelete.role
          }}
        />
      )}

      {/* User Detail Modal */}
      {detailModalOpen && selectedUser && (
        <UserDetailModal
          user={userDetails || selectedUser}
          isOpen={detailModalOpen}
          loading={userDetailsLoading}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedUser(null);
            setUserDetails(null);
          }}
        />
      )}

      {/* User Create Modal */}
      {createModalOpen && (
        <UserCreateModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSave={handleCreateSave}
          initialRole={createModalType === 'team' ? 'team' : 'user'}
          modalTitle={createModalType === 'team' ? 'Add Team Member' : 'Create New User'}
        />
      )}

      {/* Golden Notification */}
      <GoldenNotification
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        type={notificationData.type}
        title={notificationData.title}
        message={notificationData.message}
        duration={notificationData.duration}
      />
    </div>
  );
};

export default UserManagement;
